import React, { useRef, useState, useEffect, useMemo } from "react";
import VerticalFlex from "layouts/flex/VerticalFlex";
import P from "components/P";
import FlexChild from "layouts/flex/FlexChild";
import HorizontalFlex from "layouts/flex/HorizontalFlex";
import { useTranslation } from "react-i18next";
import { devLog } from "utils/logger";
import styles from "./SimpleStoreDetailModal.module.css";

const SimpleStoreDetailModal = ({ store, isMapView = false, onClose, userLocationProp }) => {
    const { t } = useTranslation();
    const modalRef = useRef(null);
    const [dragPosition, setDragPosition] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [distance, setDistance] = useState(null);

    // 드래그 시작 위치 저장
    const touchStart = useRef(0);
    const startPosition = useRef(0);

    // 매장 정보 가공 - useMemo로 최적화
    const storeInfo = useMemo(() => ({
        name: store.place_name || store.storeName,
        address: store.address_name || store.address,
        latitude: store.y || store.latitudeByX,
        longitude: store.x || store.longitudeByY,
        image: store.storeImg || '/assets/images/store-placeholder.jpg',
        distance: store.distance
    }), [store]);

    // 네비게이션 URL - useMemo로 최적화
    const navigationUrls = useMemo(() => {
        if (!storeInfo.latitude || !storeInfo.longitude) return null;
        
        const storeLat = parseFloat(storeInfo.latitude);
        const storeLng = parseFloat(storeInfo.longitude);
        const storeName = encodeURIComponent(storeInfo.name);
        
        return {
            naver: {
                appUrl: `nmap://route/car?dlat=${storeLat}&dlng=${storeLng}&dname=${storeName}`,
                webUrl: `https://map.naver.com/v5/entry/driving/${storeLat},${storeLng}/${storeName}`
            },
            kakao: {
                appUrl: `kakaomap://route?ep=${storeLat},${storeLng}&en=${storeName}&by=CAR`,
                webUrl: `https://map.kakao.com/link/to/${storeName},${storeLat},${storeLng}`
            }
        };
    }, [storeInfo.latitude, storeInfo.longitude, storeInfo.name]);

    // 컴포넌트 마운트 시 사용자 위치 가져오기
    useEffect(() => {
        // 1. 이미 계산된 거리 정보가 있는지 확인
        if (storeInfo.distance) {
            setDistance(storeInfo.distance);
            return;
        }

        // 2. props로 전달된 위치 정보가 있는지 확인
        if (userLocationProp?.latitude && userLocationProp?.longitude) {
            calculateDistance(userLocationProp.latitude, userLocationProp.longitude);
            return;
        }

        // 3. 웹뷰에서 저장된 위치 정보가 있는지 확인
        if (window.userLocation?.latitude && window.userLocation?.longitude) {
            calculateDistance(window.userLocation.latitude, window.userLocation.longitude);
            return;
        }

        // 4. 브라우저 geolocation API로 위치 정보 가져오기
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    calculateDistance(position.coords.latitude, position.coords.longitude);
                },
                (error) => {
                    // 에러 발생 시 거리 정보 표시하지 않음
                }
            );
        }
    }, [userLocationProp, storeInfo.distance]);

    // 거리 계산 함수
    const calculateDistance = (userLat, userLng) => {
        if (!storeInfo.latitude || !storeInfo.longitude) return;

        try {
            const lat1 = parseFloat(userLat);
            const lon1 = parseFloat(userLng);
            const lat2 = parseFloat(storeInfo.latitude);
            const lon2 = parseFloat(storeInfo.longitude);
            
            if (isNaN(lat1) || isNaN(lon1) || isNaN(lat2) || isNaN(lon2)) return;

            // 하버사인 공식을 사용한 거리 계산 (km)
            const R = 6371; // 지구 반경 (km)
            const dLat = deg2rad(lat2 - lat1);
            const dLon = deg2rad(lon2 - lon1);
            const a =
                Math.sin(dLat / 2) * Math.sin(dLat / 2) +
                Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
                Math.sin(dLon / 2) * Math.sin(dLon / 2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            const distance = R * c; // km 단위 거리

            setDistance(distance);
        } catch (error) {
            // 거리 계산 중 오류 발생 시 거리 정보 표시하지 않음
        }
    };

    // 각도를 라디안으로 변환
    const deg2rad = (deg) => {
        return deg * (Math.PI / 180);
    };

    // 거리 표시 포맷팅 - useMemo로 최적화
    const formattedDistance = useMemo(() => {
        if (distance === null) return "";
        devLog(distance);
        
        if (distance < 1) {
            // 1km 미만은 m 단위로 표시
            return `${Math.round(distance * 1000)}m`;
        } else {
            // 1km 이상은 소수점 첫째 자리까지 km 단위로 표시
            return `${distance.toFixed(1)}km`;
        }
    }, [distance]);

    const handleTouchStart = (e) => {
        touchStart.current = e.touches[0].clientY;
        startPosition.current = dragPosition;
        setIsDragging(true);
    };

    const handleTouchMove = (e) => {
        if (!isDragging) return;
        
        const deltaY = e.touches[0].clientY - touchStart.current;
        // 위로 드래그는 제한
        const newPosition = Math.max(0, deltaY);
        setDragPosition(newPosition);
    };

    const closeWithAnimation = () => {
        setIsClosing(true);
        setTimeout(() => {
            onClose();
        }, 300);
    };

    const handleTouchEnd = () => {
        setIsDragging(false);
        // 일정 거리 이상 드래그되면 모달 닫기
        if (dragPosition > 150) {
            closeWithAnimation();
        } else {
            // 그렇지 않으면 원위치
            setDragPosition(0);
        }
    };

    // 네비게이션 처리 함수 통합
    const handleNavigation = (type) => {
        if (!navigationUrls) return;
        
        const urls = type === 'naver' ? navigationUrls.naver : navigationUrls.kakao;
        
        // 앱 URL로 먼저 시도
        window.location.href = urls.appUrl;
        
        // 앱이 없는 경우 1초 후 웹 URL로 이동
        const timeout = setTimeout(() => {
            window.location.href = urls.webUrl;
        }, 1000);
        
        // 앱으로 이동되면 웹 URL로 이동하지 않도록 처리
        window.onblur = () => {
            clearTimeout(timeout);
        };
    };

    return (
        <>
            {!isMapView && (
                <div 
                    className={`${styles.modalOverlay} ${isClosing ? styles.closing : ''}`}
                    onClick={closeWithAnimation}
                />
            )}
            <div
                ref={modalRef}
                className={styles.modal}
                style={{
                    position: 'fixed',
                    left: 0,
                    right: 0,
                    bottom: 86,
                    backgroundColor: 'white',
                    borderRadius: '24px 24px 0 0',
                    zIndex: 1000,
                    transform: `translateY(${isClosing ? '100%' : dragPosition + 'px'})`,
                    transition: isDragging ? 'none' : 'transform 0.3s ease-out',
                    boxShadow: isMapView ? '0 -4px 12px rgba(0, 0, 0, 0.1)' : 'none',
                    maxHeight: isMapView ? 'calc(50vh - 80px)' : 'calc(70vh - 60px)',
                    overflowY: 'auto',
                    animation: isClosing ? 'none' : 'slideUp 0.3s ease-out'
                }}
            >
                {/* 매장 이미지 (우측 상단에 배치) */}
                <div
                    style={{
                        position: 'absolute',
                        top: '50px',
                        right: '20px',
                        width: '70px',
                        height: '70px',
                        borderRadius: '50%',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                        zIndex: 1000,
                        overflow: 'hidden',
                        border: '3px solid white'
                    }}
                >
                    <img 
                        src={storeInfo.image}
                        alt={storeInfo.name}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                        }}
                    />
                </div>

                {/* 드래그 핸들 */}
                <div 
                    className={styles.dragHandle}
                    style={{
                        width: '100%',
                        height: '20px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'grab',
                        touchAction: 'none',
                        userSelect: 'none',
                        position: 'relative'
                    }}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className={styles.handleBar} />
                    <div
                        onClick={closeWithAnimation}
                        className={styles.closeButton}
                    >
                        ×
                    </div>
                </div>

                <VerticalFlex>
                    <FlexChild padding={"16px 20px"}>
                        <VerticalFlex gap={12} flexStart>
                            <FlexChild>
                                <HorizontalFlex alignItems="center" gap={8}>
                                    <FlexChild flex={1}>
                                        <P weight={700} size={20} color={"#333"}>{storeInfo.name}</P>
                                    </FlexChild>
                                </HorizontalFlex>
                            </FlexChild>

                            <FlexChild>
                                <VerticalFlex gap={2} flexStart>
                                    <P color={"#666"} size={14}>{storeInfo.address}</P>
                                    <P color={"#999"} size={13}>{formattedDistance}</P>
                                </VerticalFlex>
                            </FlexChild>
                        </VerticalFlex>
                    </FlexChild>

                    <FlexChild padding={"0 20px 16px 20px"}>
                        <HorizontalFlex gap={8}>
                            <FlexChild flex={1}>
                                <a
                                    href={navigationUrls?.naver.appUrl}
                                    onClick={() => handleNavigation('naver')}
                                    className={`${styles.navigationButton} ${styles.naverButton}`}
                                >
                                    {t("네이버 지도")}
                                </a>
                            </FlexChild>
                            <FlexChild flex={1}>
                                <a
                                    href={navigationUrls?.kakao.appUrl}
                                    onClick={() => handleNavigation('kakao')}
                                    className={`${styles.navigationButton} ${styles.kakaoButton}`}
                                >
                                    {t("카카오 맵")}
                                </a>
                            </FlexChild>
                        </HorizontalFlex>
                    </FlexChild>
                </VerticalFlex>

                <style jsx>{`
                    @keyframes slideUp {
                        from {
                            transform: translateY(100%);
                        }
                        to {
                            transform: translateY(0);
                        }
                    }
                `}</style>
            </div>
        </>
    );
};

export default SimpleStoreDetailModal; 